<template>
    <div class="ocr-form">
        <div class="ocr-form__header">
            <Heading
                :labelTitle="$t.__('ocr.OcrVerificationForm.verification_space')"
                showBackButton
                @go-back="cancel"
            />
            <div class="ocr-form__header__info">
                <span class="typographies-paragraph-medium-semibold">{{ documentTypeLabel }}</span>
                <template v-if="multipleVerify">
                    <span class="ocr-form__header-remaining"> | </span>
                    <!-- eslint-disable vue/no-v-html -->
                    <span
                        class="ocr-form__header-remaining"
                        v-html="
                            $t.__('ocr.OcrVerificationForm.label_remaining', {
                                current: completeRows + 1,
                                total: totalRows,
                            })
                        "
                    />
                    <!-- eslint-enable vue/no-v-html -->
                </template>
            </div>
        </div>
        <div class="ocr-form__content">
            <div class="ocr-form__content-container">
                <SplitPane
                    class="split-pane-container"
                    :initialLeft="previewInitialWidth"
                    :leftMin="minPreviewWidth"
                    :rightMin="minFormWidth"
                    :splitterWidth="splitterWidth"
                    storageKey="ocr-verification-split"
                >
                    <template #left>
                        <div v-if="loading" class="ocr-form__skeleton-container">
                            <CompassSkeleton
                                class="ocr-form__skeleton"
                                :variant="CompassEnums.SkeletonVariantType.BLOCK"
                            />
                        </div>
                        <div v-else class="ocr-form__preview">
                            <embed
                                v-if="preview.mime === 'application/pdf'"
                                :src="preview.url ?? undefined"
                                type="application/pdf"
                            />
                            <div v-else-if="preview.url" class="ocr-form__preview-image">
                                <img :src="preview.url ?? undefined" />
                            </div>
                        </div>
                    </template>

                    <!-- FORM -->
                    <template #right>
                        <OcrVerificationFormLoading v-if="loading" />
                        <div v-else class="ocr-form__sections-wrapper">
                            <CompassAlert
                                v-if="errorCode"
                                :type="CompassEnums.AlertType.ERROR"
                                :description="errorLabel"
                            />
                            <CompassLoader v-if="processing" />

                            <div v-show="!processing" class="ocr-form__sections">
                                <!-- Document -->
                                <CompassAccordion
                                    ref="documentSectionRef"
                                    class="ocr-form__card-section ocr-form__content-document"
                                    :title="$t.__('ocr.OcrVerificationForm.document_section')"
                                    :type="CompassEnums.AccordionType.FUNCTIONAL"
                                    :coloredIconProps="{ icon: 'document', color: 'teal' }"
                                    open
                                >
                                    <div class="ocr-form__content--small">
                                        <div class="ocr-form__content-line-item">
                                            <CompassFormItem
                                                :label="$t.__('common.labels.type')"
                                                required
                                                :error="errors.type"
                                            >
                                                <CompassSelect
                                                    v-model="typeModel"
                                                    :disabled="submitting || isElectronic"
                                                    :error="!!errors.type"
                                                    menuClass="ocr-form__select-fit-dropdown"
                                                >
                                                    <CompassDropdownOption
                                                        v-for="type in availableType"
                                                        :key="type.value"
                                                        :value="type.value"
                                                        :label="type.label"
                                                    />
                                                </CompassSelect>
                                            </CompassFormItem>
                                            <CompassFormItem
                                                :label="
                                                    $t.__('ocr.OcrVerificationForm.document_status')
                                                "
                                                required
                                                :error="errors.step"
                                            >
                                                <CompassSelect
                                                    v-model="stepModel"
                                                    :disabled="submitting"
                                                    :type="CompassEnums.SelectType.TAG"
                                                    :error="!!errors.step"
                                                    menuClass="ocr-form__select-fit-dropdown"
                                                >
                                                    <CompassDropdownOption
                                                        v-for="step in availableStep"
                                                        :key="step.value"
                                                        :value="step.value"
                                                        :label="step.label"
                                                        :color="step.color"
                                                    >
                                                        <CompassTag
                                                            :color="step.color"
                                                            :size="CompassEnums.TagSize.SMALL"
                                                        >
                                                            {{ step.label }}
                                                        </CompassTag>
                                                    </CompassDropdownOption>
                                                </CompassSelect>
                                            </CompassFormItem>
                                        </div>
                                        <div class="ocr-form__content-line-item">
                                            <CompassFormItem
                                                :label="
                                                    $t.__('ocr.OcrVerificationForm.supplier_name')
                                                "
                                                required
                                                :error="errors.supplier"
                                                class="ocr-form__form-item--flex"
                                            >
                                                <CompassAutoComplete
                                                    :key="supplierSelectKey"
                                                    ref="supplierSelect"
                                                    :results="supplierResults"
                                                    :defaultValues="supplierDefaultValues"
                                                    :disabled="submitting"
                                                    :multiple="false"
                                                    :multipleLimit="1"
                                                    :triggerAfter="0"
                                                    menuClass="ocr-form__select-fit-dropdown"
                                                    :placeholder="
                                                        $t.__(
                                                            'ocr.OcrVerificationForm.enter_supplier_name',
                                                        )
                                                    "
                                                    :noResultsLabel="
                                                        $t.__(
                                                            'ocr.OcrVerificationForm.no_supplier_found',
                                                        )
                                                    "
                                                    :loading="supplierLoading"
                                                    :actions="supplierActions"
                                                    :error="!!errors.supplier"
                                                    @update:modelValue="onSupplierChange"
                                                    @update-search="onSupplierSearch"
                                                    @focus="onSupplierFocus"
                                                />
                                            </CompassFormItem>
                                            <CompassFormItem
                                                :label="
                                                    $t.__('ocr.OcrVerificationForm.document_date')
                                                "
                                                required
                                                :error="errors.documentDate"
                                                class="ocr-form__form-item--date"
                                            >
                                                <CompassDatePicker
                                                    :modelValue="form.documentDate"
                                                    :disabled="submitting || isElectronic"
                                                    :formatDateDisplayed="formatDateDisplayed"
                                                    :error="!!errors.documentDate"
                                                    @update:modelValue="updateDate"
                                                />
                                            </CompassFormItem>
                                            <CompassFormItem
                                                :label="$t.__('common.labels.expected_close_date')"
                                                :required="!isCreditNote"
                                                :error="errors.dueDate"
                                                class="ocr-form__form-item--date"
                                            >
                                                <CompassDatePicker
                                                    :modelValue="form.dueDate"
                                                    :disabled="submitting || isElectronic"
                                                    :formatDateDisplayed="formatDateDisplayed"
                                                    :error="!!errors.dueDate"
                                                    @update:modelValue="updateDueDate"
                                                />
                                            </CompassFormItem>
                                        </div>

                                        <div class="ocr-form__content-line-item">
                                            <CompassFormItem
                                                :label="$t.__('common.labels.currency')"
                                                required
                                                :error="errors.currency"
                                                class="ocr-form__form-item--currency"
                                            >
                                                <CurrencyInput
                                                    :modelValue="form.currency ?? ''"
                                                    :disabled="submitting || isElectronic"
                                                    mode="symbol"
                                                    :error="!!errors.currency"
                                                    @update:modelValue="updateCurrency"
                                                />
                                            </CompassFormItem>
                                            <CompassFormItem
                                                :label="
                                                    $t.__(
                                                        'ocr.OcrVerificationForm.external_supplier_document_number',
                                                    )
                                                "
                                                class="ocr-form__form-item--document-number"
                                                :error="errors.externalDocumentName"
                                            >
                                                <CompassInput
                                                    v-model="externalDocumentNameModel"
                                                    :disabled="submitting || isElectronic"
                                                    :error="!!errors.externalDocumentName"
                                                />
                                            </CompassFormItem>
                                            <CompassFormItem
                                                v-if="analyticsMode === 'doc'"
                                                :label="
                                                    $t.__(
                                                        'document.docFormSettings.TabGeneralSettings.analytic_code_doc',
                                                    )
                                                "
                                                class="ocr-form__form-item--analytic-code"
                                            >
                                                <CompassInput
                                                    v-model="analyticCodeModel"
                                                    :disabled="submitting"
                                                />
                                            </CompassFormItem>
                                        </div>

                                        <div class="ocr-form__content-line-item">
                                            <CompassFormItem
                                                :label="$t.__('common.labels.smart_tags')"
                                                class="ocr-form__form-item--full-width"
                                            >
                                                <CompassAutoComplete
                                                    :results="tagsResults"
                                                    :defaultValues="tagDefaultValues"
                                                    :disabled="submitting"
                                                    multiple
                                                    allowCreate
                                                    :triggerAfter="1"
                                                    :placeholder="
                                                        $t.__('utilities.Tags.enter_smarttag')
                                                    "
                                                    :noResultsLabel="
                                                        $t.__('common.labels.no_result')
                                                    "
                                                    :loading="tagsLoading"
                                                    @update:modelValue="tagsUpdated"
                                                    @update-search="onTagsSearch"
                                                />
                                            </CompassFormItem>
                                        </div>

                                        <CompassButton
                                            v-if="!showNote"
                                            :label="$t.__('ocr.OcrVerificationForm.add_note')"
                                            :type="CompassEnums.ButtonType.TERTIARY"
                                            :disabled="submitting"
                                            leftIcon="plus"
                                            @click="showNote = true"
                                        />
                                        <CompassFormItem
                                            v-else
                                            :label="$t.__('common.labels.note')"
                                            class="ocr-form__form-item--full-width"
                                        >
                                            <CompassInput
                                                v-model="noteModel"
                                                class="ocr-form__note"
                                                :disabled="submitting"
                                                :type="CompassEnums.InputType.TEXTAREA"
                                                :rows="6"
                                            />
                                        </CompassFormItem>
                                    </div>
                                </CompassAccordion>

                                <CompassAccordion
                                    ref="productsSectionRef"
                                    class="ocr-form__card-section ocr-form__content-products"
                                    :title="$t.__('ocr.OcrVerificationForm.products_services')"
                                    :type="CompassEnums.AccordionType.FUNCTIONAL"
                                    :coloredIconProps="{ icon: 'modules', color: 'purple' }"
                                    open
                                >
                                    <OcrVerificationProductsTable
                                        class="ocr-form__products-table"
                                        :disabled="submitting"
                                        :isElectronic="isElectronic"
                                        :units="units"
                                        :accountingCodes="accountingCodes"
                                        :taxRates="taxRates"
                                        :warehouses="warehouses"
                                        :isStockEnabled="isStockEnabled"
                                        :isLineItemsEnabled="isLineItemsEnabled"
                                        :showAnalyticCode="analyticsMode === 'item'"
                                        :currencySymbol="currencySymbol ?? undefined"
                                        :formatDateDisplayed="formatDateDisplayed"
                                        :hint="$t.__('ocr.OcrVerificationProductsTable.hint')"
                                        :addProduct="addProduct"
                                        :requestProductDelete="requestProductDelete"
                                    />
                                    <div
                                        class="typographies-paragraph-medium-regular ocr-form__total"
                                    >
                                        <div class="ocr-form__total__amount-excluding-tax">
                                            <span>
                                                {{ $t.__('common.labels.amount_excluding_tax') }}
                                            </span>
                                            <span class="typographies-paragraph-medium-semibold">
                                                {{ formatAmount(totalWithoutTax) }}
                                            </span>
                                        </div>

                                        <template v-for="vatAmount in taxRows" :key="vatAmount.id">
                                            <div class="ocr-form__vat-amount">
                                                <span>{{ `${vatAmount.rate}%` }}</span>
                                                <span>
                                                    {{
                                                        formatAmount(
                                                            getTotalByTax(
                                                                vatAmount.id,
                                                                vatAmount.rate,
                                                            ),
                                                        )
                                                    }}
                                                </span>
                                            </div>
                                        </template>
                                        <div class="ocr-form__vat-total">
                                            <span>
                                                {{ $t.__('ocr.OcrVerificationForm.total_tax') }}
                                            </span>
                                            <span class="typographies-paragraph-medium-semibold">
                                                {{ formatAmount(totalTax) }}
                                            </span>
                                        </div>
                                        <div class="ocr-form__total-incl-tax">
                                            <span>
                                                {{ $t.__('common.labels.amount_including_tax') }}
                                            </span>
                                            <span class="typographies-paragraph-medium-semibold">
                                                {{ formatAmount(totalWithTax) }}
                                            </span>
                                        </div>
                                    </div>
                                </CompassAccordion>

                                <CompassAccordion
                                    v-if="canAddPayment"
                                    ref="paymentsSectionRef"
                                    class="ocr-form__card-section ocr-form__content-payment"
                                    :title="
                                        $t.__(
                                            isCreditNote
                                                ? 'ocr.OcrVerificationForm.refund_title'
                                                : 'ocr.OcrVerificationForm.payment_title',
                                        )
                                    "
                                    :type="CompassEnums.AccordionType.FUNCTIONAL"
                                    :coloredIconProps="{ icon: 'pig-money', color: 'orange' }"
                                    open
                                >
                                    <div class="ocr-form__content--small">
                                        <div v-for="(payment, index) of form.payments" :key="index">
                                            <div class="ocr-form__payment-header">
                                                <h3 class="typographies-heading-h3">
                                                    {{
                                                        $t.__(
                                                            isCreditNote
                                                                ? 'ocr.OcrVerificationForm.refund_item_title'
                                                                : 'ocr.OcrVerificationForm.payment_item_title',
                                                            { number: index + 1 },
                                                        )
                                                    }}
                                                </h3>
                                                <CompassIconButton
                                                    icon="bin"
                                                    :disabled="submitting"
                                                    :variant="
                                                        CompassEnums.IconButtonVariant.DESTRUCTIVE
                                                    "
                                                    :size="CompassEnums.IconButtonSize.MEDIUM"
                                                    @click="deletePayment(index)"
                                                />
                                            </div>
                                            <div class="ocr-form__content-line-item">
                                                <CompassFormItem
                                                    :label="
                                                        $t.__(
                                                            isCreditNote
                                                                ? 'ocr.OcrVerificationForm.refund_date'
                                                                : 'ocr.OcrVerificationForm.payment_date',
                                                        )
                                                    "
                                                    :required="isPaymentPartiallyFilled(payment)"
                                                    :error="getPaymentError(index, 'date')"
                                                    class="ocr-form__form-item--date"
                                                >
                                                    <CompassDatePicker
                                                        :modelValue="payment.date"
                                                        :disabled="submitting"
                                                        :formatDateDisplayed="formatDateDisplayed"
                                                        :error="!!getPaymentError(index, 'date')"
                                                        @update:modelValue="
                                                            (value: string) =>
                                                                updatePaymentField(
                                                                    index,
                                                                    'date',
                                                                    value,
                                                                )
                                                        "
                                                    />
                                                </CompassFormItem>
                                                <CompassFormItem
                                                    :label="
                                                        $t.__(
                                                            isCreditNote
                                                                ? 'ocr.OcrVerificationForm.refund_reference'
                                                                : 'ocr.OcrVerificationForm.payment_reference',
                                                        )
                                                    "
                                                    class="ocr-form__form-item--payment-reference"
                                                >
                                                    <CompassInput
                                                        :modelValue="payment.reference"
                                                        :disabled="submitting"
                                                        @update:modelValue="
                                                            (value: string) =>
                                                                updatePaymentField(
                                                                    index,
                                                                    'reference',
                                                                    value,
                                                                )
                                                        "
                                                    />
                                                </CompassFormItem>
                                                <CompassFormItem
                                                    :label="$t.__('common.labels.amount')"
                                                    :required="isPaymentPartiallyFilled(payment)"
                                                    :error="getPaymentError(index, 'amount')"
                                                    class="ocr-form__form-item--payment-amount"
                                                >
                                                    <CompassInput
                                                        :modelValue="payment.amount"
                                                        :disabled="submitting"
                                                        inputmode="decimal"
                                                        :error="!!getPaymentError(index, 'amount')"
                                                        @update:modelValue="
                                                            (value: string | number | null) =>
                                                                updatePaymentField(
                                                                    index,
                                                                    'amount',
                                                                    limitToTwoDecimals(value),
                                                                )
                                                        "
                                                        @blur="formatPaymentAmountOnBlur(index)"
                                                    >
                                                        <template v-if="currencySymbol" #suffix>
                                                            {{ currencySymbol }}
                                                        </template>
                                                    </CompassInput>
                                                </CompassFormItem>
                                                <CompassFormItem
                                                    :label="
                                                        $t.__(
                                                            isCreditNote
                                                                ? 'common.labels.refund_method'
                                                                : 'common.labels.payment_method',
                                                        )
                                                    "
                                                    :required="isPaymentPartiallyFilled(payment)"
                                                    :error="getPaymentError(index, 'payment_mean')"
                                                    class="ocr-form__form-item--payment-method"
                                                >
                                                    <CompassSelect
                                                        :modelValue="payment.payment_mean"
                                                        :disabled="submitting"
                                                        filterable
                                                        :error="
                                                            !!getPaymentError(index, 'payment_mean')
                                                        "
                                                        :placeholder="
                                                            $t.__(
                                                                isCreditNote
                                                                    ? 'ocr.OcrVerificationForm.select_refund_method'
                                                                    : 'ocr.OcrVerificationForm.select_payment_method',
                                                            )
                                                        "
                                                        @update:modelValue="
                                                            (value: string | number) =>
                                                                updatePaymentField(
                                                                    index,
                                                                    'payment_mean',
                                                                    value,
                                                                )
                                                        "
                                                    >
                                                        <CompassDropdownOption
                                                            :value="undefined"
                                                            :label="
                                                                $t.__('common.labels.none_option')
                                                            "
                                                        />
                                                        <CompassDropdownOption
                                                            v-for="item in paymentTypes"
                                                            :key="item.id"
                                                            :label="item.label"
                                                            :value="item.id"
                                                        />
                                                    </CompassSelect>
                                                </CompassFormItem>
                                            </div>
                                        </div>
                                        <CompassButton
                                            :label="
                                                $t.__(
                                                    isCreditNote
                                                        ? 'ocr.OcrVerificationForm.add_refund'
                                                        : 'ocr.OcrVerificationForm.add_payment',
                                                )
                                            "
                                            :type="CompassEnums.ButtonType.TERTIARY"
                                            :disabled="submitting"
                                            leftIcon="plus"
                                            @click="addPayment"
                                        />
                                    </div>
                                </CompassAccordion>
                            </div>
                        </div>
                    </template>
                </SplitPane>
            </div>
        </div>

        <div class="ocr-form__footer">
            <div class="ocr-form__footer-actions">
                <CompassButton
                    :label="
                        isElectronic
                            ? $t.__('common.actions.refuse')
                            : $t.__('common.actions.delete')
                    "
                    :type="CompassEnums.ButtonType.TERTIARY_DESTRUCTIVE"
                    :disabled="submitting || isElectronic"
                    data-bot="purchases-purinvoices-verify_delete"
                    @click="deleteOCR"
                />
                <CompassButton
                    :label="$t.__('common.actions.ignore')"
                    :type="CompassEnums.ButtonType.SECONDARY"
                    :disabled="submitting"
                    data-bot="purchases-purinvoices-verify_ignore"
                    @click="skip"
                />
                <CompassButton
                    nativeType="button"
                    :label="
                        isElectronic
                            ? $t.__('common.actions.approve')
                            : $t.__('common.actions.validate')
                    "
                    :type="CompassEnums.ButtonType.PRIMARY"
                    :loading="submitting"
                    data-bot="purchases-purinvoices-verify_validate"
                    @click="submit"
                />
            </div>
        </div>
        <DeleteOcrProductLineModal
            :visible="productDeleteModalVisible"
            :rememberChoice="productDeleteRememberChoice"
            @update:visible="productDeleteModalVisible = $event"
            @update:rememberChoice="productDeleteRememberChoice = $event"
            @confirm="onProductDeleteConfirm"
            @cancel="onProductDeleteCancel"
        />
    </div>
</template>

<script setup lang="ts">
import {
    computed,
    inject,
    nextTick,
    onBeforeUnmount,
    ref,
    watch,
    type ComponentPublicInstance,
    type ComputedRef,
    type Ref,
} from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { storeToRefs } from 'pinia';
import { cloneDeep, isNil } from 'lodash';

import Heading from 'vcomponents/common/Heading/Heading.vue';
import CurrencyInput from 'vcomponents/inputs/CurrencyInput.vue';
import OcrVerificationProductsTable from 'vcomponents/ocr/OcrVerificationProductsTable.vue';
import OcrVerificationFormLoading from 'vcomponents/ocr/OcrVerificationFormLoading.vue';
import DeleteOcrProductLineModal from 'vcomponents/ocr/DeleteOcrProductLineModal.vue';
import SplitPane from 'vcomponents/layout/SplitPane.vue';

import { goToUrl } from 'vueDir/helpers';
import { hydrateOcr } from 'Services/Ocr/Ocr.hydrator';
import { persistOcr } from 'Services/Ocr/Ocr.persistor';
import {
    AMOUNT_DECIMAL_PLACES,
    calculateTotalByTax,
    calculateTotalTax,
    calculateTotalWithTax,
    calculateTotalWithoutTax,
    createEmptyProduct,
    getUniqueTaxRows,
    limitToTwoDecimals,
    setProductDescriptionFromAccountingCode,
} from 'composables/useOcrProducts';
import { useOcrForm } from 'composables/ocr/useOcrForm';
import {
    isPaymentPartiallyFilled,
    type OcrFormValues,
    type OcrPayment,
} from 'composables/ocr/ocrFormSchema';

import Ocr from 'api/ocr';
import Companies from 'api/companies';
import Feature from 'Services/Feature';
import { useOcrVerificationStore } from 'piniaStores/ocr/ocrVerificationStore';

import { useOcrDictionaries } from 'composables/ocr/useOcrDictionaries';
import { useOcrSupplierSearch } from 'composables/ocr/useOcrSupplierSearch';
import { useOcrTags } from 'composables/ocr/useOcrTags';
import { useOcrNavigationQueue } from 'composables/ocr/useOcrNavigationQueue';
import { useDateUtils } from 'composables/useDateUtils';
import {
    OcrFormStep,
    OcrFormType,
    type AnalyticsMode,
    type OcrPreview,
    type OcrProduct,
    type OcrStepOption,
    type OcrTypeOption,
} from 'composables/ocr/types';
import type { ErrorsHandler, RootState } from 'types/store/rootState.type';
import type { TaxRate } from 'types/entities/accounting/taxRate.type';
import type { Unit } from 'types/entities/units/unit.type';
import type { Currency } from 'types/entities/system/currency.type';
import type { FormTag } from 'types/entities/smartTags/tag.type';
import type { PresetSupplier } from 'types/entities/company/third.type';
import type { PushToast } from 'types/injects/pushToast.type';
import type { Translator } from 'types/injects/translator.type';
import { ThirdEvent, type ThirdCreatedPayload } from 'types/events/third.events';

// Props
interface Props {
    canCreateSupplier: boolean;
    linkedtype: string;
    linkedid: number;
}
const props = defineProps<Props>();

// Injections
const $t = inject('$t') as Translator;
const $pushToast = inject('$pushToast') as PushToast;

// Router & store
const router = useRouter();
const store = useStore();
const ocrVerificationStore = useOcrVerificationStore();

// Static layout constants
const previewInitialWidth = 640;
const minPreviewWidth = 360;
const minFormWidth = 520;
const splitterWidth = 12;
const urlBackToList = '/ocr/list/purInvoice';

// Refs
const documentSectionRef = ref<ComponentPublicInstance | null>(null);
const productsSectionRef = ref<ComponentPublicInstance | null>(null);
const paymentsSectionRef = ref<ComponentPublicInstance | null>(null);
const loading = ref(true);
const processing = ref(false);
const submitting = ref(false);
const showNote = ref(false);
const preview = ref<OcrPreview>({ mime: null, url: null });
const provider = ref('');
const analyticsMode = ref<AnalyticsMode>(null);
const currencySymbol = ref<string | null>(null);
const errorCode = ref<number | null>(null);
const ocrId = ref<string | null>(null);
const defaultAccountingCodeCompany = ref<number | null>(null);
const productDeleteModalVisible = ref(false);
const productDeleteIndex = ref<number | null>(null);
const productDeleteRememberChoice = ref(false);

const isOcrFormType = (value: string): value is OcrFormType =>
    Object.values<string>(OcrFormType).includes(value);

const initialType: OcrFormType = isOcrFormType(props.linkedtype)
    ? props.linkedtype
    : OcrFormType.INVOICE;
const initialStep = initialType === OcrFormType.CREDIT_NOTE ? OcrFormStep.STORED : OcrFormStep.DUE;

const initialFormValues = {
    type: initialType,
    supplier: null,
    documentDate: '',
    dueDate: '',
    externalDocumentName: '',
    analyticCode: '',
    currency: null,
    step: initialStep,
    note: '',
    products: [createEmptyProduct()],
    payments: [],
    tags: [],
    totalWithoutTax: null,
    totalWithTax: null,
} satisfies OcrFormValues;

const ocrForm = useOcrForm({ initialValues: initialFormValues });
const {
    values: form,
    errors: rawErrors,
    submitCount,
    handleSubmit,
    resetForm,
    productsArray,
    paymentsArray,
} = ocrForm;

// Errors are only displayed once the user has attempted to submit at least once.
const errors = computed<Partial<Record<string, string>>>(() =>
    submitCount.value > 0 ? rawErrors.value : {},
);

const sectionRefByField: Record<string, Ref<ComponentPublicInstance | null>> = {
    type: documentSectionRef,
    step: documentSectionRef,
    supplier: documentSectionRef,
    documentDate: documentSectionRef,
    dueDate: documentSectionRef,
    currency: documentSectionRef,
    externalDocumentName: documentSectionRef,
    analyticCode: documentSectionRef,
    note: documentSectionRef,
    tags: documentSectionRef,
    products: productsSectionRef,
    payments: paymentsSectionRef,
};

const scrollToFirstError = async (): Promise<void> => {
    await nextTick();
    const firstErrorPath = Object.keys(errors.value)[0];
    if (!firstErrorPath) {
        return;
    }
    const fieldName = firstErrorPath.split(/[.[]/)[0];
    const sectionEl = sectionRefByField[fieldName]?.value?.$el;
    if (sectionEl instanceof HTMLElement) {
        sectionEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
};

// Writable model refs for top-level fields used with v-model.
// vee-validate's `values` is read-only; `defineField` returns a writable ref.
const [typeModel] = ocrForm.defineField('type');
const [stepModel] = ocrForm.defineField('step');
const [externalDocumentNameModel] = ocrForm.defineField('externalDocumentName');
const [analyticCodeModel] = ocrForm.defineField('analyticCode');
const [noteModel] = ocrForm.defineField('note');

const updatePaymentField = <K extends keyof OcrPayment>(
    index: number,
    field: K,
    value: OcrPayment[K],
): void => {
    const current = paymentsArray.fields.value[index]?.value;
    if (!current) {
        return;
    }
    paymentsArray.update(index, { ...current, [field]: value });
};

const formatPaymentAmountOnBlur = (index: number): void => {
    const current = paymentsArray.fields.value[index]?.value;
    if (!current) {
        return;
    }
    const value = current.amount;
    if (value === null || value === '') {
        return;
    }
    const parsed = typeof value === 'number' ? value : parseFloat(String(value).replace(',', '.'));
    if (!Number.isNaN(parsed)) {
        updatePaymentField(
            index,
            'amount',
            parsed.toFixed(AMOUNT_DECIMAL_PLACES).replace('.', ','),
        );
    }
};

// Vuex bridges (legacy stores not yet migrated to Pinia)
const rootState = computed<RootState>(() => store.state as RootState);
const units = computed<Unit[]>(() => rootState.value.corp.units);
const uiLang = computed<string>(() => rootState.value.corp.uiLang);
const allCurrencies = computed<Currency[]>(() => rootState.value.system.currencies);
const errorsHandler = computed<ErrorsHandler>(() => rootState.value.system.errorsHandler);
const { permissions } = storeToRefs(ocrVerificationStore);

// Dictionaries
const {
    accountingCodes,
    taxRates,
    paymentTypes,
    warehouses,
    defaultAccountingCode,
    presetSupplier,
    defaultTags,
    loadDictionaries,
} = useOcrDictionaries();

// Navigation queue
const { multipleVerify, totalRows, completeRows, goNextOcr, clearQueue, rowsExcluding } =
    useOcrNavigationQueue();

// Derived references for composables
const isCreditNote = computed<boolean>(() => form.type === OcrFormType.CREDIT_NOTE);
const supplierIdRef = computed<number | null>(() => form.supplier);
const canCreateSupplierRef = computed<boolean>(() => props.canCreateSupplier);
const formTagsRef = computed<FormTag[]>({
    get: () => form.tags,
    set: (value: FormTag[]) => {
        ocrForm.setFieldValue<'tags'>('tags', value);
    },
});

// Supplier search
const {
    supplierResults,
    supplierLoading,
    supplierActions,
    supplierSelectKey,
    remoteSupplier,
    supplierDefaultValues,
    onSupplierSearch,
    onSupplierFocus,
    bumpSupplierSelectKey,
} = useOcrSupplierSearch({
    presetSupplier,
    currentSupplierId: supplierIdRef,
    canCreateSupplier: canCreateSupplierRef,
});

// Tags
const { tagsResults, tagsLoading, tagDefaultValues, onTagsSearch, tagsUpdated } = useOcrTags({
    defaultTags,
    formTags: formTagsRef,
});

const { formatDateDisplayed } = useDateUtils();

// Computed
const isStockEnabled = computed<boolean>(() => Feature.isEnabled('ocr-stock'));
const isLineItemsEnabled = computed<boolean>(
    () => Feature.isEnabled('ocr-line-items') && Feature.isEnabled('ocr-mistral'),
);
const canCreateInvoice = computed<boolean>(() => Boolean(permissions.value['create_pur_invoice']));
const canCreateCreditNote = computed<boolean>(() =>
    Boolean(permissions.value['create_pur_credit_note']),
);
const canAddPayment = computed<boolean>(() => Boolean(permissions.value['create_payment']));

const isElectronic = computed<boolean>(() => provider.value === 'facturx');

const availableType = computed<OcrTypeOption[]>(() =>
    [
        {
            label: $t.__('common.labels.invoice'),
            value: OcrFormType.INVOICE,
            enabled: canCreateInvoice.value,
        },
        {
            label: $t.__('common.labels.creditnote'),
            value: OcrFormType.CREDIT_NOTE,
            enabled: canCreateCreditNote.value,
        },
    ].filter((type) => type.enabled !== false),
);

const availableStep = computed<OcrStepOption[]>(() => {
    if (isCreditNote.value) {
        return [
            {
                label: $t.__('ocr.OcrVerificationForm.step_draft'),
                value: OcrFormStep.DRAFT,
                color: 'purple',
            },
            {
                label: $t.__('ocr.OcrVerificationForm.step_stored'),
                value: OcrFormStep.STORED,
                color: 'yellow',
            },
        ];
    }
    const steps = [
        {
            label: $t.__('ocr.OcrVerificationForm.step_draft'),
            value: OcrFormStep.DRAFT,
            color: 'purple',
        },
        {
            label: $t.__('ocr.OcrVerificationForm.step_due'),
            value: OcrFormStep.DUE,
            color: 'yellow',
        },
    ];
    return isElectronic.value ? steps.filter((step) => step.value !== OcrFormStep.DRAFT) : steps;
});

const defaultTaxRateId = computed<number | null>(() =>
    taxRates.value.length > 0 ? taxRates.value[0].id : null,
);

const totalWithoutTax = computed<number>(() => calculateTotalWithoutTax(form.products));

const taxRows: ComputedRef<TaxRate[]> = computed(() =>
    getUniqueTaxRows(form.products, taxRates.value),
);

const totalTax = computed<number>(() => calculateTotalTax(form.products, taxRows.value));

const totalWithTax = computed<number>(() =>
    calculateTotalWithTax(totalWithoutTax.value, totalTax.value),
);

const documentTypeLabel = computed<string>(() => {
    if (isElectronic.value) {
        return isCreditNote.value
            ? $t.__('ocr.OcrVerificationForm.type_electronic_credit_note')
            : $t.__('ocr.OcrVerificationForm.type_electronic_invoice');
    }
    return isCreditNote.value
        ? $t.__('ocr.OcrVerificationForm.type_imported_credit_note')
        : $t.__('ocr.OcrVerificationForm.type_imported_invoice');
});

const errorLabel = computed<string>(() => {
    const errorMessage =
        errorCode.value === 1
            ? $t.__('ocr.OcrVerificationForm.error_too_many_pages')
            : $t.__('ocr.OcrVerificationForm.error_technical');
    const manualEntryMessage = $t.__('ocr.OcrVerificationForm.error_manual_entry');
    return `${errorMessage} ${manualEntryMessage}`;
});

// Methods
const formatAmount = (value: number | null | undefined): string => {
    const options: Intl.NumberFormatOptions = form.currency
        ? {
              minimumFractionDigits: AMOUNT_DECIMAL_PLACES,
              maximumFractionDigits: AMOUNT_DECIMAL_PLACES,
              style: 'currency',
              currency: form.currency,
          }
        : {
              minimumFractionDigits: AMOUNT_DECIMAL_PLACES,
              maximumFractionDigits: AMOUNT_DECIMAL_PLACES,
              style: 'decimal',
          };
    return new Intl.NumberFormat(uiLang.value, options).format(value ?? 0);
};

const getTotalByTax = (taxId: number, taxRate: string): number =>
    calculateTotalByTax(form.products, taxId, taxRate);

const getPaymentError = (index: number, field: 'date' | 'amount' | 'payment_mean'): string => {
    return errors.value[`payments[${index}].${field}`] ?? '';
};

const updateAccountingCodeProduct = async (supplierId: number): Promise<void> => {
    const response = await Companies.getCompanyById(supplierId, {
        field: ['accounting_purchase_code_id'],
    });
    const data = response.data as { accounting_purchase_code_id: number | null };
    defaultAccountingCodeCompany.value =
        data.accounting_purchase_code_id === null
            ? defaultAccountingCode.value
            : data.accounting_purchase_code_id;
    for (const [index, field] of productsArray.fields.value.entries()) {
        if (!field.value.catalogueLink) {
            const next: OcrProduct = {
                ...field.value,
                accountingCode: defaultAccountingCodeCompany.value,
            };
            setProductDescriptionFromAccountingCode(next, accountingCodes.value);
            productsArray.update(index, next);
        }
    }
};

const updateSupplier = (supplierId: number): void => {
    void updateAccountingCodeProduct(supplierId);
    ocrForm.setFieldValue<'supplier'>('supplier', supplierId);
};

const onSupplierChange = (selected: { value: number } | null): void => {
    if (!selected) {
        ocrForm.setFieldValue<'supplier'>('supplier', null);
        bumpSupplierSelectKey();
        return;
    }
    updateSupplier(selected.value);
};

const getSupplier = async (id: number): Promise<void> => {
    processing.value = true;
    const response = await Companies.getCompanyById(id, { field: ['id', 'name'] });
    processing.value = false;
    const supplierData = response.data as PresetSupplier;
    ocrForm.setFieldValue<'supplier'>('supplier', supplierData.id);
    presetSupplier.value.push(supplierData);
    bumpSupplierSelectKey();
};

const addProduct = (product?: OcrProduct): void => {
    const newProduct = product
        ? cloneDeep(product)
        : createEmptyProduct({
              defaultAccountingCode: defaultAccountingCode.value,
              defaultTaxRateId: defaultTaxRateId.value,
              supplierId: form.supplier,
              companyAccountingCode: defaultAccountingCodeCompany.value,
          });
    productsArray.push(newProduct);
};

const addPayment = (): void => {
    paymentsArray.push({ date: '', reference: '', amount: null, payment_mean: '' });
};

const deletePayment = (index: number): void => {
    paymentsArray.remove(index);
};

const removeProduct = (index: number): void => {
    productsArray.remove(index);
};

const requestProductDelete = (index: number): void => {
    if (productDeleteRememberChoice.value) {
        removeProduct(index);
        return;
    }
    productDeleteIndex.value = index;
    productDeleteModalVisible.value = true;
};

const closeProductDeleteModal = (): void => {
    productDeleteModalVisible.value = false;
    productDeleteIndex.value = null;
};

const onProductDeleteConfirm = (): void => {
    if (productDeleteIndex.value !== null) {
        removeProduct(productDeleteIndex.value);
    }
    closeProductDeleteModal();
};

const onProductDeleteCancel = (): void => {
    closeProductDeleteModal();
};

const updateCurrency = (currency: Currency): void => {
    ocrForm.setFieldValue<'currency'>('currency', currency.name);
    currencySymbol.value = currency.symbol ?? null;
};

const updateDate = (date: string): void => {
    ocrForm.setFieldValue<'documentDate'>('documentDate', date);
};

const updateDueDate = (date: string): void => {
    ocrForm.setFieldValue<'dueDate'>('dueDate', date);
};

const cancel = (): void => {
    clearQueue();
    goToUrl(urlBackToList);
};

const skip = (): void => {
    processing.value = true;
    if (multipleVerify.value) {
        const remaining = rowsExcluding(ocrId.value);
        if (remaining.length) {
            goNextOcr(remaining, 0);
            return;
        }
    }
    clearQueue();
    goToUrl(urlBackToList);
};

const deleteOCR = async (): Promise<void> => {
    processing.value = true;
    await Ocr.deleteOcr(ocrId.value);
    processing.value = false;
    const remaining = rowsExcluding(ocrId.value);
    if (remaining.length && multipleVerify.value) {
        goNextOcr(remaining, 0);
        return;
    }
    clearQueue();
    goToUrl(urlBackToList);
};

const submit = handleSubmit(
    async (validatedValues) => {
        if (submitting.value) {
            return;
        }
        submitting.value = true;

        const formData = persistOcr(
            {
                ...validatedValues,
                totalWithoutTax: totalWithoutTax.value,
                totalWithTax: totalWithTax.value,
            },
            { currencies: allCurrencies.value, taxRates: taxRates.value },
        );

        try {
            const response = (await Ocr.submitVerification(
                ocrId.value,
                validatedValues.type,
                formData,
            )) as { docid: number };
            const remaining = rowsExcluding(ocrId.value);
            if (remaining.length && multipleVerify.value) {
                goNextOcr(remaining, 0);
                return;
            }

            const isMultiple = multipleVerify.value;
            clearQueue();

            const documentKind = isCreditNote.value ? 'credit_note' : 'invoice';
            const cardinality = isMultiple ? 'multiple' : 'single';
            const messageKey = `ocr.OcrVerificationForm.success_${documentKind}_${cardinality}`;

            $pushToast({
                type: CompassEnums.ToastType.SUCCESS,
                icon: true,
                text: $t.__(messageKey),
                duration: 5000,
            });

            if (isMultiple) {
                const listRoute = isCreditNote.value ? 'purchasecreditnotes' : 'purchaseinvoices';
                router.push({ name: listRoute });
            } else {
                const overviewRoute = isCreditNote.value
                    ? 'purchases_purCreditNoteOverview'
                    : 'purchases_purInvoiceOverview';
                goToUrl(`/?_f=${overviewRoute}&id=${response.docid}`);
            }
        } catch (error) {
            errorsHandler.value.logException(error as Error);
            $pushToast({
                type: CompassEnums.ToastType.ERROR,
                icon: true,
                text: $t.__('ocr.OcrVerificationForm.error_generic'),
                duration: 5000,
            });
            submitting.value = false;
        }
    },
    () => {
        void scrollToFirstError();
    },
);

const getOcrById = async (): Promise<void> => {
    try {
        const response = await Ocr.getOcrById(ocrId.value);
        if (response.state === 'error') {
            errorCode.value = response.error_code;
        } else {
            provider.value = response.provider || '';
            const { formData, analyticsMode: hydratedAnalyticsMode } = hydrateOcr(
                response.response,
                {
                    taxList: taxRates.value,
                    accountingCodes: accountingCodes.value,
                    defaultAccountingCode: defaultAccountingCode.value,
                    paymentTypes: paymentTypes.value,
                },
            ) as { formData: Partial<OcrFormValues>; analyticsMode: AnalyticsMode };
            analyticsMode.value = hydratedAnalyticsMode;
            resetForm({ values: { ...initialFormValues, ...formData } });
            if (response.linked_type) {
                ocrForm.setFieldValue<'type'>('type', response.linked_type);
            }
            currencySymbol.value =
                allCurrencies.value.find((currency) => form.currency === currency.name)?.symbol ??
                null;
            const supplierData = response.response.supplier;
            if (supplierData?.id !== null && supplierData?.value !== null) {
                const alreadyExists = presetSupplier.value.some(
                    (supplier) => supplier.id === supplierData.id,
                );
                if (!alreadyExists) {
                    presetSupplier.value.push({
                        id: parseInt(supplierData.id, 10),
                        name: supplierData.value,
                    });
                }
            } else if (supplierData?.value !== null) {
                remoteSupplier.value = supplierData.value;
            }
            if (!isNil(form.supplier)) {
                await updateAccountingCodeProduct(form.supplier);
            }
        }
        if (!isNil(response.preview)) {
            preview.value = response.preview;
        }
        loading.value = false;
    } catch (error) {
        router.push({
            name: 'error.not.found',
            params: {
                title: $t.__('ocr.OcrVerificationForm.document_not_found'),
                buttonOptions: {
                    label: $t.__('ocr.OcrVerificationForm.back_to_list'),
                    url: urlBackToList,
                },
            },
        } as never);
        loading.value = false;
        errorsHandler.value.logException(error as Error);
    }
};

// Watchers
watch(
    () => form.type,
    () => {
        const validSteps = availableStep.value.map((step) => step.value);
        if (!validSteps.includes(form.step)) {
            ocrForm.setFieldValue<'step'>('step', validSteps[1] ?? validSteps[0]);
        }
    },
);

// Init (equivalent to Options API `created`)
ocrId.value = props.linkedid ? String(props.linkedid) : null;

void ocrVerificationStore.loadMeta();

const onThirdCreated = (payload: ThirdCreatedPayload): void => {
    void getSupplier(payload.thirdid);
};
EventBus.on(ThirdEvent.CREATED, onThirdCreated);

void loadDictionaries().then(() => getOcrById());

onBeforeUnmount(() => {
    EventBus.off(ThirdEvent.CREATED, onThirdCreated);
});
</script>

<style lang="scss">
.page-purchase-incoming-verification {
    #sellsy_app {
        background-color: var(--colors-common-background-app);
    }
    #contentDiv {
        overflow: hidden;
    }
    .contentMargin {
        margin: calc(-1 * var(--spacing-5)) calc(-1 * var(--spacing-4));
    }
    #legacy-container {
        height: 100%;
    }
}

body:has(.subscribebar):has(.ocr-form) {
    #contentDiv {
        height: calc(100% - 40px);
    }
}

#compass-dropdown-container {
    .ocr-form__select-fit-dropdown {
        width: auto;
    }
}
</style>

<style lang="scss" scoped>
.ocr-form {
    display: flex;
    flex-direction: column;
    height: calc(100% + 2 * var(--spacing-5));

    &__form-item--flex {
        flex: 1;
    }

    &__form-item--date {
        width: 180px;
    }

    &__form-item--currency {
        max-width: 150px;
    }

    &__form-item--document-number {
        flex: 1;
        min-width: 265px;
    }

    &__form-item--analytic-code {
        width: 200px;
    }

    &__form-item--full-width {
        width: 100%;
    }

    &__form-item--payment-reference {
        flex: 1;
        min-width: 160px;
    }

    &__form-item--payment-amount {
        max-width: 130px;
    }

    &__form-item--payment-method {
        max-width: 190px;
    }

    &__header {
        background-color: var(--colors-common-background-default);
        border-bottom: solid 1px var(--colors-common-border-default);
        padding: var(--spacing-4);
        display: flex;
        align-items: center;
        flex-direction: row;
        gap: var(--spacing-1);

        :deep(.heading) {
            flex: 1;
            &.heading--h1 {
                margin: 0;
            }
        }

        &__info {
            gap: var(--spacing-1);
        }
    }

    &__header-remaining {
        line-height: var(--line-heights-24);
        font-size: var(--font-size-14);
        color: var(--colors-common-text-standard-caption);
    }

    &__content {
        flex: 1;
        overflow: hidden;
        display: flex;
        flex-direction: row;
        align-items: stretch;
        width: 100%;

        &--small {
            max-width: 720px;
        }
    }

    h3 {
        margin: 0;
    }

    &__products-table {
        border-bottom: 1px solid var(--colors-common-border-lighter);
        padding-bottom: var(--spacing-1);
        margin-bottom: var(--spacing-3);
    }

    &__vat-amount {
        display: flex;
        width: 100%;
        justify-content: space-between;
        color: var(--colors-common-text-subtle-default);
    }

    &__vat-total {
        display: flex;
        width: 100%;
        justify-content: space-between;
        margin: var(--spacing-1) 0;
    }

    &__total {
        width: 312px;
        display: flex;
        flex-direction: column;
        margin-left: auto;

        &__amount-excluding-tax {
            display: flex;
            width: 100%;
            justify-content: space-between;
            padding-bottom: var(--spacing-1);
            border-bottom: 1px solid var(--colors-common-border-lighter);
            margin-bottom: var(--spacing-1);
        }

        &-incl-tax {
            display: flex;
            width: 100%;
            justify-content: space-between;
            background-color: var(--colors-common-background-subtle);
            padding: var(--spacing-1);
            border-radius: var(--spacing-0);
        }
    }

    &__content-container {
        display: flex;
        flex-direction: row;
        width: 100%;
        height: 100%;

        :deep(.split-pane-container) {
            display: flex;
            flex-direction: row;
            justify-content: center;
            width: 100%;
            height: 100%;
            align-items: stretch;
        }

        :deep(.split-pane-container .split-pane__pane) {
            height: 100%;
            box-sizing: border-box;
        }

        :deep(.split-pane-container .split-pane__pane--right) {
            padding: var(--spacing-4);
            overflow-y: auto;
            overflow-x: hidden;
        }
    }

    &__sections {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-4);
    }

    &__card-section {
        background: var(--colors-common-background-default);
        border-radius: var(--spacing-1);
        box-shadow: var(--elevation-1);
        padding: var(--spacing-4);
    }

    &__content-line-item {
        display: flex;
        flex-direction: row;
        align-items: end;
        gap: var(--spacing-3);
        margin-bottom: var(--spacing-4);
        flex-wrap: wrap;

        :deep(.compass-form-item__input) {
            > div {
                width: 100%;
            }
        }
    }

    &__note {
        width: 100%;

        :deep(.compass-input__inner) {
            width: 100%;
        }
    }

    &__payment-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: var(--spacing-2);
    }

    &__skeleton {
        width: 100%;
        height: 100%;
        :deep(.compass-skeleton__block) {
            height: 100%;
        }
        &-container {
            height: 100%;
            background-color: var(--colors-common-background-default);
            padding: var(--spacing-4);
        }
    }

    &__preview {
        > embed {
            width: 100%;
            height: 100%;
        }
        &-image {
            width: 100%;
            height: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
            overflow: hidden;
            background-color: #282828;

            img {
                width: 100%;
                height: 100%;
                object-fit: contain;
                object-position: center center;
            }
        }
    }

    &__footer {
        background-color: var(--colors-common-background-default);
        padding: var(--spacing-3) var(--spacing-4);
        box-shadow: var(--box-shadows-medium);
        z-index: 1;
        &-actions {
            display: flex;
            justify-content: flex-end;
            align-items: center;
            gap: var(--spacing-2);
        }
    }
}
</style>
